import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useApolloClient, useMutation } from '@apollo/client'
import camelCase from 'lodash/camelCase'
import uniq from 'lodash/uniq'
import { useFormContext, useWatch } from 'react-hook-form'

import { combineDateAndTime } from 'helpers/date'
import { CREATE_AVAILABILITY_MUTATION, GET_AVAILABILITY_PRICE } from './graphql'
import { DEFAULT_START_TIME, DEFAULT_END_TIME } from 'components/ContactRequestFormComponents/constants'

import DateTimerangePicker from 'components/DateTimerangePicker'
import FormattedMessage from 'components/FormattedMessage'
import Hint from 'components/Hint'

import './MeetingpackageAvailabilityChecker.less'

const defaultDateTimerangePickerStatus = {
  variant: 'notice',
  spin: false,
  icon: 'info-circle-bordered',
  message: <FormattedMessage id="contactRequest.meetingpackage.response.default" />,
}

const MeetingpackageAvailabilityChecker = (props) => {
  const {
    initialData,
    provider: {
      id: providerId,
    },
  } = props

  const intl = useIntl()
  const apolloClient = useApolloClient()
  const form = useFormContext()
  const [dateTimerangePickerStatus, setDateTimerangePickerStatus] = useState(defaultDateTimerangePickerStatus)
  const [createAvailability] = useMutation(CREATE_AVAILABILITY_MUTATION)

  const numberOfPersons = useWatch({ name: 'numberOfPersons', defaultValue: initialData.numberOfPersons })
  const timesStartsAt = useWatch({
    name: 'timesStartsAt',
    defaultValue: initialData.timesStartsAt || DEFAULT_START_TIME,
  })
  const timesEndsAt = useWatch({
    name: 'timesEndsAt',
    defaultValue: initialData.timesEndsAt || DEFAULT_END_TIME,
  })
  const requestedDates = useWatch({
    name: 'requestedDates',
    defaultValue: initialData.requestedDates?.[0] || initialData.requestedDates,
  })
  const meetingRoomDataFieldValues = {
    numberOfPersons,
    timesStartsAt,
    timesEndsAt,
    requestedDates,
  }

  const initializeAvailabilityUpdate = () => {
    if (Object.values(meetingRoomDataFieldValues).filter(v => !v).length > 0) {
      return setDateTimerangePickerStatus(defaultDateTimerangePickerStatus)
    }

    form.trigger(Object.keys(meetingRoomDataFieldValues))
      .then((isValid) => {
        if (isValid) {
          requestAvailabilityId(meetingRoomDataFieldValues)
        } else {
          const errors = Object.keys(meetingRoomDataFieldValues)
            .map((key) => form.formState.errors[key]?.message)
            .filter(Boolean)

          return setDateTimerangePickerStatus({
            ...defaultDateTimerangePickerStatus,
            variant: 'error',
            message: uniq(errors).join(', '),
          })
        }
      })
  }

  useEffect(() => {
    initializeAvailabilityUpdate()
  }, Object.values(meetingRoomDataFieldValues))

  const requestAvailabilityId = (meetingRoomDataFieldValues) => {
    setDateTimerangePickerStatus({
      ...defaultDateTimerangePickerStatus,
      icon: 'refresh',
      spin: true,
      variant: 'notice',
      message: <FormattedMessage id="contactRequest.meetingpackage.response.refresh" />,
    })

    createAvailability({
      variables: {
        eventStartsAt: combineDateAndTime(
          meetingRoomDataFieldValues.requestedDates,
          meetingRoomDataFieldValues.timesStartsAt,
          true,
        ).toISOString(),
        eventEndsAt: combineDateAndTime(
          meetingRoomDataFieldValues.requestedDates,
          meetingRoomDataFieldValues.timesEndsAt,
          true,
        ).toISOString(),
        guestCount: parseInt(meetingRoomDataFieldValues.numberOfPersons),
        providerId,
      },
    }).then((availabilityData) => {
      setDateTimerangePickerStatus({
        ...defaultDateTimerangePickerStatus,
        icon: 'check-circle-thin',
        variant: 'success',
        message: <FormattedMessage id="contactRequest.meetingpackage.response.success" />,
      })

      const { availabilityId, grossPrice, netPrice } = (
        availabilityData.data.createMeetingpackageAvailability.availability
      )

      form.setValue('availabilityId', availabilityId)
      form.setValue('grossPrice', grossPrice)
      form.setValue('netPrice', netPrice)

      apolloClient.writeQuery({
        query: GET_AVAILABILITY_PRICE,
        data: { availabilityPrice: { grossPrice, netPrice } },
      })
    }).catch((errors) => {
      const errorFields = errors.graphQLErrors[0]?.extensions?.errors || []
      const defaultErrorMessage = intl.formatMessage({ id: 'contactRequest.meetingpackage.response.genericError' })
      let errorMessage = (
        <FormattedMessage
          id="contactRequest.meetingpackage.response.genericError"
          tagName="span"
          html
        />
      )

      if (errorFields.length) {
        errorMessage = (
          <FormattedMessage
            id={`contactRequest.meetingpackage.response.${camelCase(errorFields[0].messages[0])}`}
            tagName="span"
            defaultMessage={defaultErrorMessage}
            html
          />
        )
      }

      form.setValue('availabilityId', undefined)
      form.setValue('grossPrice', undefined)
      form.setValue('netPrice', undefined)

      setDateTimerangePickerStatus({
        ...defaultDateTimerangePickerStatus,
        variant: 'error',
        message: errorMessage,
      })
    })
  }

  const cssPrefix = 'contact-request__event-details__meetingpackate-availability-checker'

  return (
    <>
      <DateTimerangePicker
        initialData={initialData}
        hideErrors={true}
        className={`${cssPrefix}__picker`}
      />

      <input type="hidden" {...form.register('availabilityId', { required: true })} />
      <input type="hidden" {...form.register('grossPrice', { required: true })} />

      <div className={`${cssPrefix}__date-timerange-picker-messages`}>
        <Hint
          variant={dateTimerangePickerStatus.variant}
          spin={dateTimerangePickerStatus.spin}
          iconName={dateTimerangePickerStatus.icon}
          className={`${cssPrefix}__hint`}
        >
          {dateTimerangePickerStatus.message}
        </Hint>

        <Hint variant="notice" className={`${cssPrefix}__hint`}>
          <FormattedMessage id="contactRequest.meetingpackage.timeBasedPriceHint" />
        </Hint>
      </div>
    </>
  )
}

MeetingpackageAvailabilityChecker.propTypes = {
  initialData: PropTypes.shape({
    numberOfPersons: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    timesStartsAt: PropTypes.string,
    timesEndsAt: PropTypes.string,
    requestedDates: PropTypes.array,
  }).isRequired,
  provider: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }),
}

export default MeetingpackageAvailabilityChecker
